import { useAuth0 } from '@auth0/auth0-react';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { fetcher } from '@utils/client';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useSWR from 'swr';
import { identifyUser, resetUser, trackEvent } from '../lib/analytics/posthog';
import { EVENTS } from '../lib/analytics/events';

const LEX_LUTHOR = process.env.NEXT_PUBLIC_LEX_LUTHOR_URL;

// Marca local de «este navegador inició sesión en el marketplace». La escribe /callback al
// terminar el login y se borra al salir o cuando Auth0 dice que ya no hay sesión.
const SESSION_HINT_KEY = 'auth_session_hint';

// Errores con los que Auth0 responde un pedido silencioso cuando no hay sesión: son el caso
// normal de un visitante sin cuenta, no una falla.
const NO_SESSION_ERRORS = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token'
]);
const isNoSessionError = (error) => NO_SESSION_ERRORS.has(error?.error);

export const markSessionHint = () => {
  try {
    window.localStorage.setItem(SESSION_HINT_KEY, '1');
  } catch {
    // Sin localStorage (modo privado estricto): el próximo pedido silencioso no se hará.
  }
};

const clearSessionHint = () => {
  try {
    window.localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // Nada que limpiar.
  }
};

const hasSessionHint = () => {
  try {
    return (
      // Vuelta del login: Auth0 ya tiene la sesión y hay que levantarla acá.
      window.location.pathname.startsWith('/callback') ||
      window.localStorage.getItem(SESSION_HINT_KEY) === '1' ||
      // Sesiones iniciadas antes de que existiera la marca.
      Boolean(JSON.parse(window.localStorage.getItem('session') || 'null'))
    );
  } catch {
    return false;
  }
};

const AuthContext = createContext({
  isAuthenticated: false,
  isAuth0Loading: false,
  isLoading: false,
  session: null,
  reloadSession: () => null,
  logOut: () => null,
  logIn: () => null
});
AuthContext.displayName = 'AuthContext';

export const AuthProvider = ({ children }) => {
  const { user, getAccessTokenSilently, isAuthenticated, logout, isLoading: isAuth0Loading } = useAuth0();
  const session = useLocalStorage('session', null);
  const redirectUri = useLocalStorage('redirectUri', null);
  const [token, setToken] = useState(null);

  const checkAuth0Session = async () => {
    try {
      const token = await getAccessTokenSilently();
      setToken(token);
    } catch (error) {
      // Sin sesión en Auth0 (venció o se cerró en otra app): no es un error, se deja de intentar.
      if (isNoSessionError(error)) {
        clearSessionHint();
        session.setValue(null);
        return;
      }
      // Don't swallow silently: a swallowed failure here leaves the app in a
      // permanent isAuthenticated:true + session:null state (blank profile /
      // infinite spinner). Retry once forcing a fresh token before giving up.
      console.warn('getAccessTokenSilently failed, retrying', error);
      try {
        const token = await getAccessTokenSilently({ cacheMode: 'off' });
        setToken(token);
      } catch (retryError) {
        if (isNoSessionError(retryError)) {
          clearSessionHint();
          session.setValue(null);
          return;
        }
        console.error('getAccessTokenSilently retry failed', retryError);
      }
    }
  };

  // El login es con auth0-js (/signin → /callback), así que el SDK de React no sabe que hay
  // sesión hasta que este pedido silencioso la levanta. Pero hacerlo en cada visita anónima abría
  // dos iframes ocultos a auth.agora.red y dejaba dos `login_required` en la consola de todos.
  // Solo se intenta si hay rastro de una sesión: la marca que deja /callback, la sesión guardada
  // de antes o el propio SDK diciendo que está autenticado.
  // Solo cuando cambia el estado de Auth0: `checkAuth0Session` se recrea en cada render y con ella en
  // las dependencias el pedido se repetiría en cada render.
  useEffect(() => {
    if (isAuth0Loading) return;
    if (!isAuthenticated && !hasSessionHint()) return;
    checkAuth0Session();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAuth0Loading]);

  const isDifferentUser = user?.email !== session.storedValue?.email;

  const url = `${process.env.NEXT_PUBLIC_CYCLONE_API_URL}/sessions`;
  const fetcher = (url) =>
    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then((res) => res.json());
  const {
    data: result,
    error,
    isLoading,
    mutate
  } = useSWR(token ? url : null, fetcher, {
    onSuccess: (data) => {
      session.setValue({ ...data, isAuthenticated: true });
      if (data?.id) {
        identifyUser(data.id, {
          email: data.email,
          name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
          is_vendor: !!data.vendor_id,
          user_type: data.vendor_id ? 'vendor' : 'client'
        });
      }
    },
    onError: (e) => {
      toast.error('Error al cargar la sesión');
      session.setValue(null);
    },
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  // const { isLoading, refetch } = useQuery(
  //   ['session'],
  //   async () => await client<SessionI>('sessions', 'GET', { isAuthRequired: true }),
  //   {
  //     retry: false,
  //     refetchOnWindowFocus: false,
  //     enabled: user !== undefined && (session.storedValue === null || isDifferentUser),
  //     onSuccess: (data: any) => session.setValue({ ...data, isAuthenticated: true }),
  //     // FIXME: Handle onError case
  //     onError: () => null
  //   }
  // );

  const logOut = useCallback(() => {
    trackEvent(EVENTS.AUTH_LOGGED_OUT);
    resetUser();
    redirectUri.setValue(window.location.pathname + window.location.search);

    clearSessionHint();
    session.deleteValue('session');
    logout({
      logoutParams: {
        returnTo: `${LEX_LUTHOR}/logout`
      }
    });

    // window.location.href = window.location.pathname + window.location.search;
  }, [session, logout, redirectUri]);

  const logIn = useCallback(
    (args) => {
      redirectUri.setValue(
        args.redirectUri ? args.redirectUri : window.location.pathname + window.location.search
      );
      // A /signin con recarga completa: el login es con auth0-js, fuera del router de Next.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      window.location.href = `${LEX_LUTHOR}/signin`;
    },
    [redirectUri]
  );

  const value = useMemo(() => {
    const contextValue = {
      isAuthenticated: true,
      isLoading,
      session: session.storedValue,
      getAccessTokenSilently,
      logOut,
      logIn,
      reloadSession: mutate,
      isAuth0Loading
    };
    if (!isAuthenticated) {
      const result = { ...contextValue, isAuthenticated: false, session: null };
      return result;
    } else {
      const result = {
        ...contextValue,
        isAuthenticated: true,
        session: session.storedValue
      };
      return result;
    }
  }, [isAuthenticated, isLoading, session, getAccessTokenSilently, logOut, logIn, isAuth0Loading, mutate]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }

  return context;
};
